import React, { useEffect, useMemo, useState } from 'react';
import {
	Box,
	ButtonBase,
	Card,
	CardContent,
	Chip,
	Collapse,
	Divider,
	Grid,
	IconButton,
	Stack,
	TextField,
	Typography
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import { FaTshirt } from 'react-icons/fa';
import { GiUnderwearShorts } from 'react-icons/gi';
import { getProximosEventos } from '../../services/apis';

const emptyEventos = {
	fecha: '',
	comidas: [],
	actividades: [],
	limpieza: [],
	ropa: []
};

const formatearFechaLocal = (date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');

	return `${year}-${month}-${day}`;
};

const crearFechaLocal = (fecha) => {
	const [year, month, day] = String(fecha).split('-').map(Number);
	return new Date(year, month - 1, day);
};

const RopaIcon = ({ item }) => {
	const descripcion = item.descripcion?.toLowerCase() || '';
	const color = item.color_ropa || '#1a202c';

	if (descripcion.includes('pant')) {
		return <GiUnderwearShorts color={color} size={24} />;
	}

	if (descripcion.includes('samarreta')) {
		return <FaTshirt color={color} size={22} />;
	}

	return <span className="acon-color-dot" style={{ backgroundColor: color }} />;
};

export const AconSemana = () => {
	const [eventos, setEventos] = useState(emptyEventos);
	const [loaded, setLoaded] = useState(false);
	const [fecha, setFecha] = useState(null);
	const [desplegar, setDesplegar] = useState(false);

	const cambiarFecha = (event) => {
		setFecha(event.target.value);
	};

	const desplazarFecha = (dias) => {
		if (!fecha) return;
		const nuevaFecha = crearFechaLocal(fecha);
		nuevaFecha.setDate(nuevaFecha.getDate() + dias);
		setFecha(formatearFechaLocal(nuevaFecha));
	};

	const cargarEventos = async (fechaSeleccionada) => {
		try {
			const response = await getProximosEventos(fechaSeleccionada);

			if (response.success) {
				setEventos({ ...emptyEventos, ...response.data });
				setFecha(fechaSeleccionada || response.fecha);
				setLoaded(true);
			}
		} catch (error) {
			console.error('Error:', error);
		}
	};

	useEffect(() => {
		cargarEventos(fecha);
	}, [fecha]);

	const fechaLegible = useMemo(() => {
		if (!fecha) return 'Agenda';
		return new Date(`${fecha}T00:00:00`).toLocaleDateString('ca-ES', {
			weekday: 'long',
			day: '2-digit',
			month: 'short'
		});
	}, [fecha]);

	const secciones = [
		{
			key: 'comidas',
			titulo: 'Menjars',
			icono: <LocalDiningIcon fontSize="small" color='warning' />,
			items: eventos.comidas,
			renderTitulo: (item) => item.comida || item.descripcion || 'Menjar',
			renderDetalle: (item) => item.cocineros?.length ? `Encarregat: ${item.cocineros.join(', ')}` : item.descripcion
		},
		{
			key: 'actividades',
			titulo: 'Activitats',
			icono: <LocalActivityIcon fontSize="small" color='primary' />,
			items: eventos.actividades,
			renderTitulo: (item) => item.actividad || item.descripcion || 'Activitat',
			renderDetalle: (item) => item.descripcion
		},
		{
			key: 'limpieza',
			titulo: 'Neteja',
			icono: <CleaningServicesIcon fontSize="small" color='info' />,
			items: eventos.limpieza,
			renderTitulo: (item) => item.descripcion || 'Neteja',
			renderDetalle: (item) => item.limpiadores?.length ? `Per: ${item.limpiadores.join(', ')}` : null
		},
		{
			key: 'ropa',
			titulo: 'Roba',
			icono: <CheckroomIcon fontSize="small" color='error' />,
			items: eventos.ropa,
			renderTitulo: (item) => item.descripcion || 'Roba',
			renderDetalle: () => null
		}
	];

	const totalEventos = secciones.reduce((total, seccion) => total + seccion.items.length, 0);

	return (
		<Grid container size={{ xs: 12 }} className="acon-shell">
			{loaded && (
				<Card className="acon-card">
					<CardContent className="acon-content">
						<Stack className="acon-date-row" direction="row">
							<IconButton className="acon-nav-button" onClick={() => desplazarFecha(-1)}>
								<ArrowBackIosNewIcon fontSize="small" />
							</IconButton>

							<Box className="acon-date-copy">
								<Typography className="acon-eyebrow">Agenda del dia</Typography>
								<Typography variant="h6" className="acon-date-title">
									{fechaLegible}
								</Typography>
							</Box>

							<TextField
								type="date"
								name="fecha"
								id="fecha"
								size="small"
								value={fecha || ''}
								onChange={cambiarFecha}
								className="acon-date-input"
							/>

							<IconButton className="acon-nav-button" onClick={() => desplazarFecha(1)}>
								<ArrowForwardIosIcon fontSize="small" />
							</IconButton>
						</Stack>

						<Stack className="acon-summary" direction="row">
							{secciones.map((seccion) => (
								<Chip
									key={seccion.key}
									icon={seccion.icono}
									label={`${seccion.titulo} ${seccion.items.length}`}
									className={`acon-chip acon-chip-${seccion.key}`}
								/>
							))}
						</Stack>

						<ButtonBase
							type="button"
							className="acon-toggle"
							onClick={() => setDesplegar((value) => !value)}
						>
							<span>{totalEventos === 0 ? 'No hi ha esdeveniments previstos' : `${totalEventos} esdeveniments previstos`}</span>
							<KeyboardArrowDownIcon className={desplegar ? 'acon-toggle-icon acon-toggle-icon-open' : 'acon-toggle-icon'} />
						</ButtonBase>

						<Collapse in={desplegar}>
							<Box className="acon-sections">
								{secciones.filter((seccion) => seccion.items.length > 0).map((seccion) => (
									<Box key={seccion.key} className="acon-section">
										<Stack className="acon-section-title" direction="row">
											<span className={`acon-section-mark acon-section-mark-${seccion.key}`}>{seccion.icono}</span>
											<Typography variant="subtitle1">{seccion.titulo}</Typography>
										</Stack>

										<Divider className="acon-divider" />

										<Stack className="acon-list">
											{seccion.items.map((item, idx) => (
												<Box key={`${seccion.key}-${idx}`} className="acon-item">
													<span className="acon-time">{item.hora || '--:--'}</span>
													<Box className="acon-item-body">
														<Stack direction="row" className="acon-item-title-row">
															<Typography className="acon-item-title">{seccion.renderTitulo(item)}</Typography>
															{seccion.key === 'ropa' && item.color_ropa && (
																<span className="acon-ropa-icon">
																	<RopaIcon item={item} />
																</span>
															)}
														</Stack>
														{seccion.renderDetalle(item) && (
															<Typography className="acon-item-detail">
																{seccion.renderDetalle(item)}
															</Typography>
														)}
													</Box>
												</Box>
											))}
										</Stack>
									</Box>
								))}
							</Box>
						</Collapse>
					</CardContent>
				</Card>
			)}
		</Grid>
	);
};
